import { calcBetLiability, calcBetProfit } from "./clientUtils";

/** Resolve market from API field or matchName suffix "(Match Odds)" etc. */
export function resolveBetMarket(bet) {
  const raw = bet?.marketType ?? bet?.market_type ?? "";
  let m = String(raw).toLowerCase().trim().replace(/\s+/g, "_");
  if (m === "mo") m = "match_odds";
  if (m === "bm" || m === "normal") m = "bookmaker";
  if (m === "fancy" || m === "bookmaker" || m === "match_odds") return m;

  const name = String(bet?.matchName ?? bet?.match_name ?? "");
  if (/\(\s*fancy\s*\)/i.test(name) || /\bfancy\b/i.test(name)) return "fancy";
  if (/\(\s*match\s*odds\s*\)/i.test(name)) return "match_odds";
  if (/\(\s*bookmaker\s*\)/i.test(name) || /\(\s*normal\s*\)/i.test(name)) return "bookmaker";
  return "bookmaker";
}

/**
 * Per-runner P&L if that selection wins.
 * Only counts bets for `marketFilter` (bookmaker | match_odds) so Normal
 * and Match Odds each show their own position — never mixed.
 */
export function calcRunnerPnl(bets, runnerName, allRunnerNames = [], marketFilter = null) {
  const target = String(runnerName || "").trim().toLowerCase();
  const want = marketFilter ? String(marketFilter).toLowerCase().replace(/\s+/g, "_") : null;

  let pnl = 0;

  for (const bet of bets) {
    if (bet.status !== "open") continue;
    const market = resolveBetMarket(bet);
    if (market === "fancy") continue;
    if (want && market !== want) continue;

    const sel = String(bet.selection || "").trim().toLowerCase();
    const stake = Number(bet.stake) || 0;
    const odds = Number(bet.odds) || 0;
    const profit =
      Number(bet.potentialWin) ||
      Number(calcBetProfit(stake, odds, market, bet.betType)) ||
      0;
    const liability = calcBetLiability(stake, odds, market, bet.betType);
    const isTarget = sel === target;

    if (bet.betType === "back") {
      pnl += isTarget ? profit : -stake;
    } else if (bet.betType === "lay") {
      // Lay loses when selection wins → pay liability
      pnl += isTarget ? -liability : profit;
    }
  }

  return Math.round(pnl * 100) / 100;
}

/** Open fancy bets for one session line (e.g. "15 over run NOT"). */
export function getFancySessionBets(bets, sessionName) {
  const target = String(sessionName || "").trim().toLowerCase();
  if (!target) return [];

  return (bets || []).filter((b) => {
    if (b.status !== "open") return false;
    const market = resolveBetMarket(b);
    const isFancy =
      market === "fancy" ||
      /over\s*runs?/i.test(String(b.selection || "")) ||
      /fancy/i.test(String(b.matchName || b.match_name || ""));
    if (!isFancy) return false;
    return String(b.selection || "").trim().toLowerCase() === target;
  });
}

/**
 * Summarize open fancy position on a session for the odds table.
 */
export function summarizeFancySession(bets, sessionName) {
  const list = getFancySessionBets(bets, sessionName);
  let yesStake = 0;
  let noStake = 0;

  for (const bet of list) {
    const stake = Number(bet.stake) || 0;
    if (bet.betType === "back") {
      yesStake += stake;
    } else {
      noStake += stake;
    }
  }

  return {
    bets: list,
    yesStake,
    noStake,
    hasBet: list.length > 0,
  };
}

export function getMatchBets(bets, matchId) {
  const id = Number(matchId);
  return (bets || []).filter(
    (b) => Number(b.matchId) === id && b.status === "open"
  );
}
